import useOnSaveCustomForm from 'customForm/hooks/useOnSaveCustomForm'
import { CustomFormAnyData } from 'customForm/types'
import { JsonFormsDebuggable } from 'elements/jsonForms/JsonFormsDebuggable'
import _ from 'lodash'
import React, { useMemo, useRef } from 'react'
import { useForm } from 'react-final-form'
import { useDebouncedCallback } from 'util/Debounce'
import { FormJSONType, HostedDisclosureType } from '../types'

type PropTypes = {
  onClose: () => void
  integration: string
  disclosures: HostedDisclosureType[]
  form_json?: FormJSONType | null
  initialValues: object
  setJsonFormData: (data: CustomFormAnyData) => void
  jsonFormData: CustomFormAnyData
}

const HostedJSONSchemaCreditApp: React.FC<PropTypes> = (props) => {
  // Store previous disclosures to prevent unnecessary schema recalculations
  // This is critical on mobile where viewport resizes (virtual keyboard) can trigger re-renders
  // and cause JsonForms to remount fields, losing focus
  const prevDisclosuresRef = useRef<HostedDisclosureType[] | null>(null)
  const prevDisclosuresStringRef = useRef<string>('')

  // Create a stable string representation of disclosures for comparison
  const disclosuresString = useMemo(() => {
    if (!props.disclosures || props.disclosures.length === 0) return ''
    return JSON.stringify(
      //@ts-ignore
      props.disclosures.map((d) => ({ id: d.id, name: d.name, text: d.text, requireConsent: d.requireConsent }))
    )
  }, [props.disclosures])

  // Only recalculate if disclosures actually changed (deep comparison)
  const disclosuresChanged = disclosuresString !== prevDisclosuresStringRef.current
  if (disclosuresChanged) {
    prevDisclosuresStringRef.current = disclosuresString
    prevDisclosuresRef.current = props.disclosures
  }
  const stableDisclosures = prevDisclosuresRef.current || props.disclosures

  const form = useForm()

  const onSave = useOnSaveCustomForm({ parentForm: form })

  const onChange = useDebouncedCallback((newData, errors) => {
    const oldData = props.jsonFormData

    if (!oldData || oldData === newData) return

    if (stableDisclosures?.length) {
      if (!('disclosures' in newData)) {
        newData.disclosures = stableDisclosures
      }
      // @ts-ignore
      if (newData?.disclosures !== oldData?.disclosures) {
        newData.disclosures = stableDisclosures
      }
    }

    const hasChangesAndSaved = onSave({ currentData: oldData, newData })
    props.setJsonFormData(newData)
    if (!hasChangesAndSaved) return
  }, 300)

  // const onChange = useCallback(
  //   (newFormVals, errors) => {
  //     // JSON Forms dispatches onChange whenever data prop is updated, not just when there has been user input
  //     // It also clones the data object, making it impossible to check by reference
  //     // Without this check, the change handler can be fired recursively, causing input fields to flash/reset
  //     // This is especially problematic on mobile devices where focus/blur events are more sensitive

  //     // Extract the current form data (without disclosures) for comparison
  //     let currentFormData = props.jsonFormData
  //     if (stableDisclosures?.length && 'disclosures' in props.jsonFormData) {
  //       // Create a copy without disclosures for comparison
  //       currentFormData = { ...props.jsonFormData }
  //       // @ts-ignore
  //       delete currentFormData.disclosures
  //     }

  //     // Compare only the form values (excluding disclosures) to prevent unnecessary updates
  //     if (_.isEqual(newFormVals, currentFormData)) return

  //     if (stableDisclosures?.length) {
  //       // this is only used for lightreach. But we take the fetched disclosures that we got from lightreach when the
  //       // application opened and add them to form state. This allows our backend to ensure all required disclosures
  //       // have been agreed to by the customer and it protects against us from disclosures changing on the LR side
  //       // after an application has been submitted
  //       props.setJsonFormData({ ...newFormVals, disclosures: stableDisclosures })
  //     } else {
  //       props.setJsonFormData(newFormVals)
  //     }
  //   },
  //   [stableDisclosures, props.setJsonFormData, props.jsonFormData]
  // )

  const formSchemaWithDisclosures = useMemo(() => {
    if (!stableDisclosures || stableDisclosures.length === 0) return props.form_json?.schema
    let updatedSchema = _.cloneDeep(props.form_json?.schema)
    stableDisclosures.forEach((disc) => {
      // @ts-ignore
      updatedSchema.properties[`applicants[0].disclosures.${disc.id}`] = {
        type: 'object',
        properties: {
          ExpandableTerms: {
            // @ts-ignore
            name: disc.name,
            // @ts-ignore
            text: disc.text,
            // @ts-ignore
            id: disc.id,
            // @ts-ignore
            requireConsent: disc.requireConsent,
          },
        },
      }
      //@ts-ignore
      updatedSchema.properties[`applicants[1].disclosures.${disc.id}`] = {
        type: 'object',
        properties: {
          ExpandableTerms: {
            // @ts-ignore
            name: disc.name,
            // @ts-ignore
            text: disc.text,
            // @ts-ignore
            id: disc.id,
            // @ts-ignore
            requireConsent: disc.requireConsent,
          },
        },
      }
    })
    return updatedSchema
  }, [props.form_json?.schema, disclosuresString])

  const uiSchemaWithDisclosures = useMemo(() => {
    if (!stableDisclosures || stableDisclosures.length === 0) return props.form_json?.uiSchema
    let updatedUiSchema = _.cloneDeep(props.form_json?.uiSchema)

    let disclosuresElementsPrimary = stableDisclosures.map((disc) => {
      return {
        type: 'ExpandableTerms',
        //@ts-ignore
        scope: `#/properties/applicants[0].disclosures.${disc.id}`,
        label: 'I agree to the terms and conditions',
      }
    })

    const disclosureLayoutPrimary = {
      type: 'VerticalLayout',
      elements: disclosuresElementsPrimary,
    }
    // @ts-ignore
    updatedUiSchema.elements[0].elements.push(disclosureLayoutPrimary)

    let disclosuresElementsSecondary = stableDisclosures.map((disc) => {
      return {
        type: 'ExpandableTerms',
        //@ts-ignore
        scope: `#/properties/applicants[1].disclosures.${disc.id}`,
        label: 'I agree to the terms and conditions',
      }
    })

    const disclosureLayoutSecondary = {
      type: 'VerticalLayout',
      elements: disclosuresElementsSecondary,
    }

    // @ts-ignore
    if (updatedUiSchema.elements.length > 2) {
      // @ts-ignore
      updatedUiSchema.elements[2].elements.push(disclosureLayoutSecondary)
    }

    return updatedUiSchema
  }, [props.form_json?.uiSchema, disclosuresString])
  if (!formSchemaWithDisclosures || !uiSchemaWithDisclosures) return null
  return (
    <div>
      <JsonFormsDebuggable
        schema={formSchemaWithDisclosures}
        uischema={uiSchemaWithDisclosures}
        data={props.jsonFormData}
        onChange={onChange}
        config={{ integration: props.integration }}
      />
    </div>
  )
}
export default HostedJSONSchemaCreditApp
